import React from 'react';
import styled, { css } from 'styled-components';
import { LFC } from '../../../Legacy';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Icon } from '../../../concepts/resources/Icon';
import { useResolve } from '../../Container';
import { EditorSession } from '../../canvas/EditorSession';
import { createObserver } from '../../canvas/ExtendableStore';
import { InlineWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Colors, Float, toRem } from './../../../ui';
import { ActiveCallInfo } from './ActiveCallInfo';
import { callLeave, callStartOrJoin } from './CallCommands';
import { LocalCallTile } from './CallTiles';

const animTime = 800;

const SpotLoader: LFC<{ variant?: 'light' | 'dark' }> = ({ variant }) =>
    <Spinner>
        <Bouncer mode="A" color={variant === 'dark' ? '#555' : 'white'} />
        <Bouncer mode="B" color={variant === 'dark' ? '#555' : 'white'} />
    </Spinner>;

const Spinner = styled.div`
    position: relative;
    width: 75%;
    height: 75%;
`;

const Bouncer = styled.div<{ mode: 'A' | 'B', color: string }>`
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background-color: ${p => p.color};
    opacity: 0.6;
    position: absolute;
    top: 0;
    left: 0;

    @keyframes sk-bounce {
        0%, 100% {
            transform: scale(0.0);
            -webkit-transform: scale(0.0);
        } 50% {
            transform: scale(1.0);
            -webkit-transform: scale(1.0);
        }
    }

    animation: sk-bounce 2.0s infinite ease-in-out;
    animation-delay: ${p => p.mode === 'A' ? '0' : '-1.0s'};
`;

const RoundFloat = styled.div`
    & > *, & > * > * {
        border-radius: 1000px;
    }
`;

const FloatFrame = styled.div<{ size: any }>`
    position: relative;
    width: ${p => toRem(p.size)};
    height: ${p => toRem(p.size)};
    transition: all ${animTime}ms cubic-bezier(0,.99,.24,1);
`;

const RotateOutFrame = styled.div<{ active?: boolean }>`
    position: absolute;
    top: 50%;
    left: 50%;
    transition: all ${animTime / 4}ms ease-out;

    ${p => p.active ? css`
        transform: translate(-50%, -50%) scale(.5);
        opacity: 0;
        pointer-events: none;
    ` : css`
        transform: translate(-50%, -50%);
        opacity: 1;
    `}
`;

const LoadingFrame = styled.div<{ active?: boolean }>`
    position: relative;
    width: 100%;
    height: 100%;
    background: ${Colors.positive};
    border-radius: 1000px;
    overflow: hidden;
    color: ${Colors.text};
    font-size: 1.25rem;
    font-weight: 450;
    transition: all ${animTime / 2}ms ease-out;
    pointer-events: none;

    & > * {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    ${p => p.active ? css`
        opacity: 1;
        transform: scale(1);
    ` : css`
        opacity: 0;
        transform: scale(.5);
    `}
`;

const StartCallButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    color: ${Colors.positive};
    border-radius: 1000px;
    user-select: none;
    cursor: pointer;
    
    &:hover {
        background: ${Colors.hover};
    }
    &:active {
        background: ${Colors.active};
    }
`;

const EndCallButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 12rem;
    height: 12rem;
    color: ${Colors.negative};
    border-radius: 1000px;
    user-select: none;
    cursor: pointer;

    svg {
        display: none;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    &:hover {
        background: black;
        & > * {
            opacity: 0.5;
        }
        & > svg {
            opacity: 1;
            display: block;
        }
    }
    &:active {
        background: black;
        & > * {
            opacity: 0.25;
        }
        & > svg {
            opacity: 1;
            display: block;
        }
    }
`;

const OfflineView: LFC<{ call: ActiveCallInfo }> = ({ call }) => {
    const dispatch = useResolve(CommandDispatcher);

    return <RoundFloat>
        <Float>
            <FloatFrame size={call.key === 'none' ? 3 : 12}>
                <RotateOutFrame active={call.key !== 'none'}>
                    <StartCallButton onClick={() => dispatch(callStartOrJoin)}>
                        <Icon size="2" src="deqo://icons/hero/phone" />
                    </StartCallButton>
                </RotateOutFrame>
                <LoadingFrame active={call.key !== 'none'}>
                    <SpotLoader />
                    <span>Joining...</span>
                </LoadingFrame>
            </FloatFrame>
        </Float>
    </RoundFloat>;
};

const OnlineView: LFC<{ call: ActiveCallInfo }> = ({ call }) => {
    const dispatch = useResolve(CommandDispatcher);
    return <RoundFloat>
        <Float>
            <EndCallButton onClick={() => dispatch(callLeave)}>
                <LocalCallTile id="local" size="12" {...call.members.local} />
                <Icon size="4" src="deqo://icons/hero/phone" />
            </EndCallButton>
        </Float>
    </RoundFloat>;
};

const CallObserver = createObserver(ActiveCallInfo);

export const CallWidget: LFC<{ widget: InlineWidget }> = () =>
    <CallObserver store={useResolve(EditorSession)} render={call => call.key === 'active'
        ? <OnlineView call={call} />
        : <OfflineView call={call} />
    } />;
